/**
 * yoursMetaBest — the trainer's OWN copies measured against the meta
 * (2026-09-10 owner ask: Battle Hub's "Yours" compares to My Pokémon, not to
 * the dex, and names the best Pokémon they hold for each meta's top 100).
 *
 * The dex only knows whether a species was ever caught. A My Pokémon record
 * knows the copy: its IVs, CP and level, its shadow state and its form. So
 * "you have a counter" becomes "you have THIS one, and here is how good it
 * is", and a top-100 entry is claimed by the best copy that can stand in
 * that league or raid line.
 *
 * PvP claims are DESTINY-AWARE, the same rule as everywhere else in the app:
 * a Froakie whose spread is a 97% Great League Greninja claims Greninja's
 * row, marked as what it becomes. Raids are claimed by the copy as it is
 * (evolving for a raid is a different, cheaper errand, and the ranking is
 * species-level anyway), so the raid picks match the exact form in the bag.
 *
 * Pure, so it unit-tests in Node.
 */
import { BATTLE_META, type BattleMetaEntry } from "./generated/battleMeta.generated";
import { evolutionChainFor } from "./generated/speciesBaseStats.generated";
import { speciesName } from "@/data/speciesNames";
import { deriveMyPokemon, type MyPokemonDerived } from "./myPokemonDerived";
import { leaguePotentialsFor } from "./bestPotential";
import { ivPercent, type MyPokemonRecord } from "./myPokemonStore";
import { NO_RAID_FILTERS, rankRaidAttackers, type RaidRanked } from "./raidMeta";
import { findRaidAttacker } from "./attackerForm";
import type { OwnsFn } from "./metaCoverage";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/** How far down each meta a copy can claim a row. */
export const YOURS_META_TOP = 100;

export type YoursLeagueKey = "great" | "ultra" | "master";

/**
 * Ownership as My Pokémon records say it: a species is owned on the base dex
 * when any copy exists, on the Shadow dex when a shadow copy exists, on the
 * Mega dex when a copy is flagged Mega. Everything else the coverage math
 * asks about (form dexes) is not a raid counter question and reads false.
 */
export function ownsFromRecords(records: readonly MyPokemonRecord[]): OwnsFn {
  const byVariant = new Map<PokedexVariant, Set<number>>();
  const add = (variant: PokedexVariant, id: number) => {
    let set = byVariant.get(variant);
    if (!set) byVariant.set(variant, (set = new Set()));
    set.add(id);
  };
  for (const r of records) {
    if (r.speciesId == null) continue;
    add("pokemon", r.speciesId);
    if (r.isShadow) add("shadow", r.speciesId);
    if (r.isMega) add("mega", r.speciesId);
  }
  return (variant, dexId) => byVariant.get(variant)?.has(dexId) ?? false;
}

export interface YourPvpPick {
  entry: BattleMetaEntry;
  record: MyPokemonRecord;
  derived: MyPokemonDerived;
  /** Percent-of-best in this league for the copy, as the claimed form. */
  percent: number | null;
  /** Set when the copy claims the row as an evolution it has not made yet. */
  asForm: { speciesId: number; speciesName: string } | null;
}

function leaguePctFor(d: MyPokemonDerived, league: YoursLeagueKey): number | null {
  if (league === "great") return d.greatEligible ? d.greatPct : null;
  if (league === "ultra") return d.ultraEligible ? d.ultraPct : null;
  return d.masterPct;
}

/**
 * The best copy the trainer holds for ONE ranked row, or null. The species
 * as it is, or a pre-evolution whose destiny in this league is that species.
 */
export function bestPvpCopyFor(
  records: readonly MyPokemonRecord[],
  league: YoursLeagueKey,
  entry: BattleMetaEntry,
): YourPvpPick | null {
  let best: YourPvpPick | null = null;
  for (const record of records) {
    if (record.speciesId == null || record.isShadow !== entry.shadow) continue;
    let candidate: YourPvpPick | null = null;
    if (record.speciesId === entry.dexId) {
      const derived = deriveMyPokemon(record);
      candidate = { entry, record, derived, percent: leaguePctFor(derived, league), asForm: null };
    } else if (evolutionChainFor(record.speciesId).includes(entry.dexId)) {
      const derived = deriveMyPokemon(record);
      const potential = leaguePotentialsFor(record, derived.level)?.[league] ?? null;
      // The destiny sweep names ONE winning form per league; the row is only
      // this record's if that form is the entry's species.
      if (!potential || potential.speciesId !== entry.dexId) continue;
      candidate = {
        entry,
        record,
        derived,
        percent: potential.percent,
        asForm: { speciesId: entry.dexId, speciesName: speciesName(entry.dexId) },
      };
    }
    if (!candidate) continue;
    if (!best || (candidate.percent ?? -1) > (best.percent ?? -1)) best = candidate;
  }
  return best;
}

/**
 * The best copies the trainer holds for a league's top 100, best rank first.
 * One pick per entry (its best copy), at most `limit` entries.
 */
export function yourPvpPicks(
  records: readonly MyPokemonRecord[],
  league: YoursLeagueKey,
  limit = 5,
): YourPvpPick[] {
  const meta = BATTLE_META.find((l) => l.key === league);
  if (!meta) return [];
  const picks: YourPvpPick[] = [];
  for (const entry of meta.entries) {
    if (entry.rank > YOURS_META_TOP) continue;
    const best = bestPvpCopyFor(records, league, entry);
    if (best) {
      picks.push(best);
      if (picks.length >= limit) break;
    }
  }
  return picks;
}

export interface YourRaidPick {
  row: RaidRanked;
  record: MyPokemonRecord;
  derived: MyPokemonDerived;
  ivPercent: number | null;
}

/**
 * The best copies the trainer holds for the neutral raid top 100 (the
 * Rating measure, every boss type), best rank first. The exact form in the
 * bag must match the ranked row: a plain Landorus does not claim Therian.
 */
/** The best copy the trainer holds for ONE ranked attacker row, or null. */
export function bestRaidCopyFor(
  records: readonly MyPokemonRecord[],
  row: RaidRanked,
): YourRaidPick | null {
  let best: YourRaidPick | null = null;
  for (const record of records) {
    if (record.speciesId == null || record.isShadow !== row.shadow) continue;
    if (record.speciesId !== row.attacker.dexId) continue;
    const attacker = findRaidAttacker(record.speciesId, record.formId);
    if (!attacker || attacker.form !== row.attacker.form) continue;
    const derived = deriveMyPokemon(record);
    const candidate: YourRaidPick = { row, record, derived, ivPercent: ivPercent(record) };
    const better =
      !best ||
      (candidate.ivPercent ?? -1) > (best.ivPercent ?? -1) ||
      ((candidate.ivPercent ?? -1) === (best.ivPercent ?? -1) &&
        (candidate.derived.level ?? -1) > (best.derived.level ?? -1));
    if (better) best = candidate;
  }
  return best;
}

export function yourRaidPicks(
  records: readonly MyPokemonRecord[],
  limit = 5,
): YourRaidPick[] {
  const { rows } = rankRaidAttackers(NO_RAID_FILTERS, "er", YOURS_META_TOP);
  const picks: YourRaidPick[] = [];
  for (const row of rows) {
    const best = bestRaidCopyFor(records, row);
    if (best) {
      picks.push(best);
      if (picks.length >= limit) break;
    }
  }
  return picks;
}
